import React, { useState } from 'react';
import {
    Modal,
    Box,
    Typography,
    IconButton,
    Avatar,
    Divider,
    Button,
    Stack,
    Chip,
    Card,
    CardContent,
    CircularProgress
} from '@mui/material';
import {
    Close,
    Download,
    InsertDriveFile,
    PictureAsPdf,
    Description,
    Image as ImageIcon,
    VideoLibrary,
    Person,
    CalendarToday,
    Category,
    Storage,
    Info
} from '@mui/icons-material';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { English } from '../../constant/EnglishFallback';

export default function ProductDocumentDetailModal({ open, onClose, document }) {
    const [isImageLoading, setIsImageLoading] = useState(true);
    const { multiLanguageData } = useSelector((state) => state?.main);

    const LanguageData = multiLanguageData?.Products || {};
    const {
        TEXT_FILE_DETAILS = English.Products.TEXT_FILE_DETAILS,
        TEXT_UPLOADED_ON = English.Products.TEXT_UPLOADED_ON,
        TEXT_FILE_INFORMATION = English.Products.TEXT_FILE_INFORMATION,
        TEXT_FILE_NAME = English.Products.TEXT_FILE_NAME,
        TEXT_FILE_TYPE = English.Products.TEXT_FILE_TYPE,
        TEXT_RELETED_TO = English.Products.TEXT_RELETED_TO,
        TEXT_FILE_DESCRIPTION = English.Products.TEXT_FILE_DESCRIPTION,
        BUTTON_CLOSE = English.Products.BUTTON_CLOSE,
        BUTTON_DOWNLOAD = English.Products.BUTTON_DOWNLOAD,
        TEXT_CLICK_DOWNLOAD = English.Products.TEXT_CLICK_DOWNLOAD
    } = LanguageData || {};

    if (!document) return null;

    const getFileIcon = (type) => {
        if (type?.includes('pdf')) return <PictureAsPdf />;
        if (type?.includes('word') || type?.includes('document')) return <Description />;
        if (type?.includes('image')) return <ImageIcon />;
        if (type?.includes('video')) return <VideoLibrary />;
        return <InsertDriveFile />;
    };

    const getFileColor = (type) => {
        if (type?.includes('pdf')) return '#e74c3c';
        if (type?.includes('word') || type?.includes('document')) return '#2980b9';
        if (type?.includes('image')) return '#27ae60';
        if (type?.includes('video')) return '#9b59b6';
        return '#95a5a6';
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return 'N/A';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    const handleDownload = () => {
        if (document.fileUrl) {
            window.open(document.fileUrl, '_blank');
        }
    };

    const isImage = document.fileType?.includes('image');
    const isVideo = document.fileType?.includes('video');
    const isPDF = document.fileType?.includes('pdf');
    const fileColor = getFileColor(document.fileType);

    const renderFilePreview = () => {
        if (isImage && document.fileUrl) {
            return (
                <Box
                    sx={{
                        width: '100%',
                        height: 300,
                        bgcolor: 'grey.100',
                        borderRadius: 2,
                        overflow: 'hidden',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    {isImageLoading && (
                        <CircularProgress
                            sx={{
                                position: 'absolute',
                                color: fileColor
                            }}
                        />
                    )}
                    <img
                        src={document.fileUrl}
                        alt={document.fileName}
                        onLoad={() => setIsImageLoading(false)}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            display: isImageLoading ? 'none' : 'block'
                        }}
                    />
                </Box>
            );
        } else if (isVideo && document.fileUrl) {
            return (
                <Box
                    sx={{
                        width: '100%',
                        height: 300,
                        bgcolor: 'black',
                        borderRadius: 2,
                        overflow: 'hidden'
                    }}
                >
                    <video
                        src={document.fileUrl}
                        controls
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain'
                        }}
                    />
                </Box>
            );
        } else if (isPDF && document.fileUrl) {
            return (
                <Box
                    sx={{
                        width: '100%',
                        height: 400,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <iframe
                        src={document.fileUrl}
                        title={document.fileName}
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none'
                        }}
                    />
                </Box>
            );
        } else {
            return (
                <Box
                    sx={{
                        width: '100%',
                        height: 200,
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        border: '2px dashed',
                        borderColor: 'divider'
                    }}
                >
                    <Avatar sx={{ bgcolor: `${fileColor}20`, width: 80, height: 80 }}>
                        {React.cloneElement(getFileIcon(document.fileType), {
                            sx: { fontSize: 40, color: fileColor }
                        })}
                    </Avatar>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {document.fileType?.split('/')[1]?.toUpperCase() || 'DOCUMENT'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {TEXT_CLICK_DOWNLOAD}
                    </Typography>
                </Box>
            );
        }
    };

    const InfoRow = ({ icon, label, value, fullWidth = false }) => (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                ...(fullWidth && { gridColumn: '1 / -1' })
            }}
        >
            <Avatar sx={{ bgcolor: 'grey.100', width: 36, height: 36 }}>
                {React.cloneElement(icon, { sx: { fontSize: 20, color: 'text.secondary' } })}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption"  sx={{color: "text.secondary",  display: 'block', mb: 0.5 }}>
                    {label}
                </Typography>
                <Typography sx={{ fontWeight: 500, wordBreak: 'break-word' }} variant="body2">
                    {value || 'N/A'}
                </Typography>
            </Box>
        </Box>
    );

    return (
        <Modal open={open} onClose={onClose}>
            <Box className="user-modal-responsive" sx={{ maxWidth: 800, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        pb: 2,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {TEXT_FILE_DETAILS}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        <IconButton
                            onClick={handleDownload}
                            disabled={!document.fileUrl}
                            color="primary"
                            sx={{
                                bgcolor: 'primary.50',
                                '&:hover': { bgcolor: 'primary.100' }
                            }}
                        >
                            <Download />
                        </IconButton>
                        <IconButton onClick={onClose}>
                            <Close />
                        </IconButton>
                    </Stack>
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, overflow: 'auto', py: 2 }}>
                    {/* User Info Card */}
                    <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                        <CardContent>
                            <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
                                <Avatar
                                    src={document.userProfilePicture}
                                    sx={{ width: 56, height: 56, border: '2px solid', borderColor: 'divider' }}
                                >
                                    <Person />
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {document.userName || 'Unknown User'}
                                    </Typography>
                                    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                                        <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                            {TEXT_UPLOADED_ON} {moment(document.dateCreated).format('MMM DD, YYYY h:mm A')}
                                        </Typography>
                                    </Stack>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>

                    {/* File Preview */}
                    <Box sx={{ mb: 3 }}>
                        {renderFilePreview()}
                    </Box>

                    {/* File Information */}
                    <Card>
                        <CardContent>
                            <Stack sx={{ alignItems: "center", mb: 2 }} direction="row" spacing={1}>
                                <Info color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {TEXT_FILE_INFORMATION}
                                </Typography>
                            </Stack>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                    gap: 3
                                }}
                            >
                                <InfoRow
                                    icon={<InsertDriveFile />}
                                    label={TEXT_FILE_NAME}
                                    value={document.fileName}
                                    fullWidth
                                />

                                <InfoRow
                                    icon={<Category />}
                                    label={TEXT_FILE_TYPE}
                                    value={document.fileType?.split('/')[1]?.toUpperCase()}
                                />

                                {document.appName && (
                                    <InfoRow
                                        icon={<Category />}
                                        label={TEXT_RELETED_TO}
                                        value={`${document.appName} - ${document.appRecordName}`}
                                        fullWidth
                                    />
                                )}

                                {document.description && (
                                    <Box sx={{ gridColumn: '1 / -1', mt: 1 }}>
                                        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                            <Description sx={{ fontSize: 20, color: 'text.secondary' }} />
                                            <Typography variant="caption"  sx={{color: "text.secondary",  fontWeight: 600 }}>
                                                {TEXT_FILE_DESCRIPTION}
                                            </Typography>
                                        </Stack>
                                        <Typography
                                            variant="body2"
                                            
                                            sx={{color: "text.secondary", 
                                                pl: 4,
                                                py: 1.5,
                                                px: 2,
                                                bgcolor: 'grey.50',
                                                borderRadius: 1,
                                                borderLeft: '3px solid',
                                                borderColor: fileColor
                                            }}
                                        >
                                            {document.description}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* Action Buttons */}
                <Box
                    sx={{
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 10
                    }}
                >
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            fullWidth
                            sx={{ height: 48 }}
                        >
                            {BUTTON_CLOSE}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Download />}
                            onClick={handleDownload}
                            disabled={!document.fileUrl}
                            fullWidth
                            sx={{
                                height: 48,
                                bgcolor: fileColor,
                                '&:hover': { bgcolor: fileColor, opacity: 0.9 }
                            }}
                        >
                            {BUTTON_DOWNLOAD}
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Modal>
    );
}
